/**
 * CRM-LIST-VIEWS-V1 (V1-P7) -- FilterBar: the REUSABLE list filter primitive
 * (registered shared component; token layer only).
 *
 * GENERIC BY CONSTRUCTION: the consumer supplies typed filter GROUPS (each a
 * server-side query param on an existing scoped read); this component only
 * renders controls and reports selections. Adding a filter NEVER widens data
 * access -- fences apply server-side before filters (E-3). Every control is
 * a labeled FIELD (the render law), keyboard-accessible, and compact
 * (desktop-operator-first, C-13).
 *
 * Groups:
 *   - 'tokens'  multi-select from a controlled vocabulary (chips-in-menu).
 *   - 'choice'  single-select from server-shaped options (e.g. assignee).
 *   - 'flag'    boolean predicate toggle.
 * Active selections render as REMOVABLE chips beside the bar (the caller
 * renders them via its contract's activeFilterChips -- one chip vocabulary).
 */

import { useEffect, useRef, useState } from 'react';

export interface FilterTokenOption {
  readonly token: string;
  readonly label: string;
}

export type FilterGroupDef =
  | {
      readonly kind: 'tokens';
      readonly key: string;
      readonly label: string;
      readonly options: readonly FilterTokenOption[];
      readonly selected: readonly string[];
    }
  | {
      readonly kind: 'choice';
      readonly key: string;
      readonly label: string;
      readonly options: readonly FilterTokenOption[];
      readonly selected: string | null;
      /** Label for the "no selection" option (default 'Any'). */
      readonly anyLabel?: string;
    }
  | {
      readonly kind: 'flag';
      readonly key: string;
      readonly label: string;
      readonly on: boolean;
    };

export interface FilterBarProps {
  readonly groups: readonly FilterGroupDef[];
  /** tokens: (key, token, nowSelected). choice: (key, value|null). flag: (key, on). */
  readonly onToggleToken: (key: string, token: string, selected: boolean) => void;
  readonly onChoice: (key: string, value: string | null) => void;
  readonly onFlag: (key: string, on: boolean) => void;
  /** Reset to the role-default view (C-11). */
  readonly onReset: () => void;
}

/** One dropdown menu for a token group (multi-select, checkbox semantics). */
function TokenGroupMenu({
  group,
  onToggleToken,
}: {
  readonly group: Extract<FilterGroupDef, { kind: 'tokens' }>;
  readonly onToggleToken: FilterBarProps['onToggleToken'];
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);
  const count = group.selected.length;
  return (
    <div className="ac-filterbar__group" ref={ref}>
      <button
        type="button"
        className={
          count > 0
            ? 'ac-filterbar__trigger ac-filterbar__trigger--active'
            : 'ac-filterbar__trigger'
        }
        aria-haspopup="true"
        aria-expanded={open}
        data-testid={`filter-trigger-${group.key}`}
        onClick={() => setOpen((v) => !v)}
      >
        {group.label}
        {count > 0 ? <span className="ac-filterbar__count">{count}</span> : null}
      </button>
      {open ? (
        <div className="ac-filterbar__menu" role="menu" data-testid={`filter-menu-${group.key}`}>
          {group.options.map((opt) => {
            const selected = group.selected.includes(opt.token);
            return (
              <label key={opt.token} className="ac-filterbar__option">
                <input
                  type="checkbox"
                  checked={selected}
                  data-testid={`filter-option-${group.key}-${opt.token}`}
                  onChange={() => onToggleToken(group.key, opt.token, !selected)}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function FilterBar({
  groups,
  onToggleToken,
  onChoice,
  onFlag,
  onReset,
}: FilterBarProps): JSX.Element {
  return (
    <div className="ac-filterbar" data-testid="ac-filterbar">
      {groups.map((group) => {
        if (group.kind === 'tokens') {
          return (
            <TokenGroupMenu key={group.key} group={group} onToggleToken={onToggleToken} />
          );
        }
        if (group.kind === 'choice') {
          return (
            <label key={group.key} className="ac-filterbar__choice">
              {group.label}:{' '}
              <select
                value={group.selected ?? ''}
                data-testid={`filter-choice-${group.key}`}
                onChange={(e) =>
                  onChoice(group.key, e.target.value === '' ? null : e.target.value)
                }
              >
                <option value="">{group.anyLabel ?? 'Any'}</option>
                {group.options.map((opt) => (
                  <option key={opt.token} value={opt.token}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          );
        }
        return (
          <label key={group.key} className="ac-filterbar__flag">
            <input
              type="checkbox"
              checked={group.on}
              data-testid={`filter-flag-${group.key}`}
              onChange={(e) => onFlag(group.key, e.target.checked)}
            />
            {group.label}
          </label>
        );
      })}
      <button
        type="button"
        className="ac-filterbar__reset"
        data-testid="filter-reset"
        onClick={onReset}
      >
        Reset
      </button>
    </div>
  );
}
